
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface UserItem {
  id: string;
  item_id: string;
  obtained_at: string;
  gacha_items: {
    name: string;
    description: string;
    image_url: string;
    rarity: 'common' | 'rare' | 'epic' | 'legendary';
  };
}

interface MyItemsProps {
  userId: string;
}

const rarityColors = {
  common: 'from-gray-400 to-gray-600',
  rare: 'from-blue-400 to-blue-600',
  epic: 'from-purple-400 to-purple-600',
  legendary: 'from-yellow-400 to-yellow-600',
};

const rarityNames = {
  common: '일반',
  rare: '레어',
  epic: '에픽',
  legendary: '전설',
};

export default function MyItems({ userId }: MyItemsProps) {
  const [items, setItems] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    fetchMyItems();
  }, [userId]);

  const fetchMyItems = async () => {
    try {
      const { data, error } = await supabase
        .from('user_gacha_history')
        .select(`
          id,
          item_id,
          obtained_at,
          gacha_items (
            name,
            description,
            image_url,
            rarity
          )
        `)
        .eq('user_id', userId)
        .order('obtained_at', { ascending: false });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('아이템 로딩 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = filter === 'all' 
    ? items 
    : items.filter(item => item.gacha_items.rarity === filter);

  const getItemCount = (rarity: string) => {
    return items.filter(item => item.gacha_items.rarity === rarity).length;
  };

  if (loading) {
    return (
      <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-xl p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white/80">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-xl p-8">
      {/* 통계 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="backdrop-blur-xl bg-white/5 border border-white/20 rounded-lg p-4">
          <div className="text-center">
            <i className="ri-treasure-map-line text-3xl text-white mb-2"></i>
            <div className="text-2xl font-extrabold text-white">{items.length}</div>
            <div className="text-white/80 text-sm">전체</div>
          </div>
        </div>

        {Object.entries(rarityNames).map(([key, name]) => (
          <div key={key} className="backdrop-blur-xl bg-white/5 border border-white/20 rounded-lg p-4">
            <div className="text-center">
              <div className={`w-8 h-8 flex items-center justify-center bg-gradient-to-r ${rarityColors[key as keyof typeof rarityColors]} rounded-lg mb-2 mx-auto`}>
                <i className="ri-star-line text-lg text-white"></i>
              </div>
              <div className="text-2xl font-extrabold text-white">{getItemCount(key)}</div>
              <div className="text-white/80 text-sm">{name}</div>
            </div>
          </div>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-extrabold transition-all whitespace-nowrap cursor-pointer ${
            filter === 'all'
              ? 'bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 text-white'
              : 'bg-white/10 text-white/80 hover:bg-white/20'
          }`}
        >
          전체
        </button>
        {Object.entries(rarityNames).map(([key, name]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-4 py-2 rounded-lg font-extrabold transition-all whitespace-nowrap cursor-pointer ${
              filter === key
                ? `bg-gradient-to-r ${rarityColors[key as keyof typeof rarityColors]} text-white`
                : 'bg-white/10 text-white/80 hover:bg-white/20'
            }`}
          >
            {name}
          </button>
        ))}
      </div>

      {/* 아이템 목록 */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-12">
          <i className="ri-inbox-line text-6xl text-white/50 mb-4"></i>
          <p className="text-white/80 text-lg">보유한 아이템이 없습니다</p>
          <p className="text-white/60 text-sm mt-2">매일 뽑기를 통해 아이템을 획득하세요!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((userItem) => (
            <div
              key={userItem.id}
              className="backdrop-blur-xl bg-white/5 border border-white/20 rounded-xl p-6 hover:bg-white/10 transition-all"
            >
              <div className={`w-full h-40 flex items-center justify-center bg-gradient-to-r ${rarityColors[userItem.gacha_items.rarity]} rounded-lg mb-4`}>
                {userItem.gacha_items.image_url ? (
                  <img
                    src={userItem.gacha_items.image_url}
                    alt={userItem.gacha_items.name}
                    className="w-32 h-32 object-contain"
                  />
                ) : (
                  <i className="ri-treasure-map-line text-6xl text-white"></i>
                )}
              </div>

              <div className="mb-2">
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-extrabold bg-gradient-to-r ${rarityColors[userItem.gacha_items.rarity]} text-white`}>
                  {rarityNames[userItem.gacha_items.rarity]}
                </span>
              </div>

              <h3 className="text-lg font-extrabold text-white mb-2">
                {userItem.gacha_items.name}
              </h3>
              <p className="text-white/80 text-sm mb-3">
                {userItem.gacha_items.description}
              </p>
              <div className="text-white/60 text-xs">
                <i className="ri-time-line mr-1"></i>
                {new Date(userItem.obtained_at).toLocaleDateString('ko-KR')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
